import React, { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { ConfirmModal } from './Modals'
import '../styles.css'

export default function LineItem({ item, onUpdate, onDelete, onAddBelow, onDragStart, userId, onHartEdit }) {
  const [desc, setDesc] = useState(item.description)
  const [unitCost, setUnitCost] = useState(item.unit_cost)
  const [qty, setQty] = useState(item.quantity)
  const [saved, setSaved] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const costRef = useRef(null)
  const qtyRef = useRef(null)

  const lineTotal = (parseFloat(unitCost) || 0) * (parseFloat(qty) || 0)

  const handleBlur = () => {
    onUpdate(item.id, {
      description: desc,
      unit_cost: parseFloat(unitCost) || 0,
      quantity: parseFloat(qty) || 0,
    })
  }

  const toggleProcurement = () => {
    onUpdate(item.id, { procurement: item.procurement === 'order' ? 'stock' : 'order' })
  }

  const saveToCatalogue = async () => {
    if (!desc.trim() || !userId) return
    await supabase.from('item_catalogue').upsert({
      user_id: userId, category: item.category, description: desc.trim(),
      unit_cost: parseFloat(unitCost) || 0, default_qty: 0, is_active: true, sort_order: 999,
    }, { onConflict: 'user_id,category,description' })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  // From cost: Tab → this row's qty
  const handleCostKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      qtyRef.current?.focus()
    }
  }

  // From qty: Tab → next row's cost input (find by data-field in DOM)
  const handleQtyKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      // Find all cost inputs in the same category body
      const allCostInputs = Array.from(
        document.querySelectorAll('input[data-field="cost"]')
      )
      const currentIndex = allCostInputs.indexOf(costRef.current)
      const next = allCostInputs[currentIndex + 1]
      if (next) {
        next.focus()
        next.select()
      }
    }
    // Shift+Tab from qty → back to cost
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      costRef.current?.focus()
      costRef.current?.select()
    }
  }

  const handleCostFocus = (e) => e.target.select()
  const handleQtyFocus = (e) => e.target.select()

  const isFav = !!(item.catalogue_item_id || saved)

  return (
    <div
      className="item-row"
      style={{ gridTemplateColumns: '22px minmax(0,1fr) 96px 64px 116px 90px' }}
    >
      {/* Drag handle */}
      <div
        className="drag"
        title="Drag to reorder"
        draggable={!!onDragStart}
        onDragStart={onDragStart}
      >
        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
          <circle cx="3" cy="3" r="1.3"/><circle cx="7" cy="3" r="1.3"/>
          <circle cx="3" cy="7" r="1.3"/><circle cx="7" cy="7" r="1.3"/>
          <circle cx="3" cy="11" r="1.3"/><circle cx="7" cy="11" r="1.3"/>
        </svg>
      </div>

      {/* Description */}
      <div style={{ minWidth: 0 }}>
        <input
          className="item-name"
          style={{ border: 'none', background: 'transparent', width: '100%', outline: 'none', padding: '2px 0', fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)' }}
          value={desc}
          onChange={e => setDesc(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key === 'Enter' && onAddBelow) {
              e.preventDefault()
              if (desc !== item.description) onUpdate(item.id, { description: desc })
              onAddBelow()
            }
          }}
          placeholder="Item description"
          tabIndex={-1}
        />
        {item.supplier && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--ink-3)', marginTop: '1px' }}>
            {item.supplier}
            {onHartEdit && (
              <button
                style={{ background: 'none', border: 'none', fontFamily: 'var(--font-mono)', fontSize: '0.58rem', color: 'var(--green)', cursor: 'pointer', padding: 0, marginLeft: '0.4rem', textDecoration: 'underline' }}
                onClick={onHartEdit}
              >
                view / edit ↗
              </button>
            )}
          </div>
        )}
      </div>

      {/* Unit cost */}
      <input
        ref={costRef}
        data-field="cost"
        className="numcell"
        value={unitCost}
        onChange={e => setUnitCost(e.target.value)}
        onBlur={handleBlur}
        onFocus={handleCostFocus}
        onKeyDown={handleCostKeyDown}
        type="number"
        step="0.01"
        min="0"
      />

      {/* Qty */}
      <input
        ref={qtyRef}
        data-field="qty"
        className="numcell"
        value={qty}
        onChange={e => setQty(e.target.value)}
        onBlur={handleBlur}
        onFocus={handleQtyFocus}
        onKeyDown={handleQtyKeyDown}
        type="number"
        step="0.5"
        min="0"
      />

      {/* Total */}
      <div className="total-cell tnum">£{lineTotal.toFixed(2)}</div>

      {/* Actions */}
      <div className="actions">
        <button
          className={`act fav${isFav ? ' on' : ''}`}
          onClick={() => {
            if (item.catalogue_item_id) {
              setConfirmModal({
                message: `Remove "${desc}" from My Items?`,
                confirmLabel: 'Remove',
                danger: true,
                onConfirm: async () => {
                  await supabase.from('item_catalogue').delete().eq('id', item.catalogue_item_id)
                  onUpdate && onUpdate(item.id, { catalogue_item_id: null })
                },
              })
            } else {
              saveToCatalogue()
            }
          }}
          title={item.catalogue_item_id ? 'In My Items — click to remove' : (saved ? 'Saved to My Items' : 'Save to My Items')}
          tabIndex={-1}
        >
          {isFav ? '★' : '☆'}
        </button>
        <button
          className="act del"
          style={{ visibility: onDelete ? 'visible' : 'hidden' }}
          onClick={() => onDelete && onDelete(item.id)}
          title="Remove"
          tabIndex={-1}
        >
          ×
        </button>
        {onAddBelow && (
          <button
            className="act"
            onClick={onAddBelow}
            title="Add row below"
            tabIndex={-1}
          >
            +
          </button>
        )}
      </div>

      <ConfirmModal config={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  )
}
